import type { IToggle } from 'unleash-proxy-client';

import type { AppState } from 'types/app';
import {
  FEATURE_ENABLED_VARIANT_NAME_TREATMENT,
  FEATURE_HOME_PAGE_KRATOS,
  FEATURE_LANDING_PAGE_KRATOS,
  FEATURE_MAFIA_AKAMAI,
  FEATURE_MARTY_CLIENT_RENDER,
  FEATURE_PAGINATION_REDESIGN,
  FEATURE_PDP_KRATOS,
  FEATURE_RETURN_POLICY_60_DAY
} from 'constants/features';
import marketplace from 'cfg/marketplace.json';

export const selectUnleashToggle = (feature: string, state: AppState): IToggle | undefined =>
  (state.unleash?.toggles || []).find((t: IToggle) => t.name === feature);

export const selectABTestEnabled = (testName: string, state: AppState, treatmentValue = '1') =>
  selectUnleashVariantByPayload(testName, state) === treatmentValue;

export const selectIsFeatureMafiaAkamai = (state: AppState) => Boolean(selectUnleashToggle(FEATURE_MAFIA_AKAMAI, state)?.enabled);

export const selectIsFeatureMartyClientRender = (state: AppState) => Boolean(selectUnleashToggle(FEATURE_MARTY_CLIENT_RENDER, state)?.enabled);

// Zappos-only: the pagination redesign does not ship to 6pm, so it is always off there.
export const selectIsPaginationRedesignEnabled = (state: AppState) =>
  !marketplace.is6pm && Boolean(selectUnleashToggle(FEATURE_PAGINATION_REDESIGN, state)?.enabled);

/**
 * `web.returnpolicy.60day` killswitch (FEE-8732). Drives the 60-day policy end to end: on-site copy,
 * the displayed return window ("return by" dates, days remaining), the return-eligible order fetch
 * ranges, the "Items Over 12 Months Old" divider, the store-credit window, and whether return/cancel
 * eligibility reads the backend `customerReturnable` / `customerCancellable` flags.
 *
 * Zappos-only: 6pm keeps its own return window regardless of the killswitch.
 */
// TODO FEE-8732: clean up killswitch
export const selectIsReturnPolicy60DayEnabled = (state: AppState) =>
  !marketplace.is6pm && Boolean(selectUnleashToggle(FEATURE_RETURN_POLICY_60_DAY, state)?.enabled);

export const selectUnleashVariantByPayload = (feature: string, state: AppState) => selectUnleashToggle(feature, state)?.variant.payload?.value;

export const selectLandingPageKratosVariant = (state: AppState) => {
  const variant = selectUnleashToggle(FEATURE_LANDING_PAGE_KRATOS, state)?.variant?.name;
  return variant || 'disabled';
};

export const selectIsHomePageExperiment = (state: AppState) => {
  const variant = selectUnleashToggle(FEATURE_HOME_PAGE_KRATOS, state)?.variant?.name;
  return variant === FEATURE_ENABLED_VARIANT_NAME_TREATMENT;
};

export const selectPdpKratosVariant = (state: AppState) => {
  const variant = selectUnleashToggle(FEATURE_PDP_KRATOS, state)?.variant?.name;
  return variant || 'control';
};
